import { useState, useEffect } from 'react';
import { Brain, Heart, Shield, Target, Clock, Lightbulb, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Trading psychology tips - 80% mindset, 20% technical
const PSYCHOLOGY_TIPS = [
  // DISCIPLINE
  {
    category: "DISCIPLINE",
    icon: Shield,
    tip: "Stick to your plan. Enter only when ALL criteria are met.",
    detail: "The best traders miss good trades to avoid bad ones.",
    color: "bg-blue-600"
  },
  {
    category: "DISCIPLINE",
    icon: Shield,
    tip: "Set your stop loss BEFORE entering. No exceptions.",
    detail: "A trade without a stop is gambling, not trading.",
    color: "bg-blue-600"
  },
  {
    category: "DISCIPLINE",
    icon: Shield,
    tip: "Risk only 1-2% per trade. Survive to trade another day.",
    detail: "Position sizing is more important than entry timing.",
    color: "bg-blue-600"
  },
  
  // EMOTIONS
  {
    category: "EMOTIONS",
    icon: Heart,
    tip: "Revenge trading turns 1 loss into 5. Walk away after 2 losses.",
    detail: "The market will be here tomorrow. Your capital might not.",
    color: "bg-red-500"
  },
  {
    category: "EMOTIONS",
    icon: Heart,
    tip: "FOMO is the #1 account killer. Missed trades don't hurt your P/L.",
    detail: "There's always another setup. Wait for YOUR edge.",
    color: "bg-red-500"
  },
  {
    category: "EMOTIONS",
    icon: Heart,
    tip: "Feeling anxious about a position? You're probably over-sized.",
    detail: "Trade small enough to think clearly.",
    color: "bg-red-500"
  },
  {
    category: "EMOTIONS",
    icon: Heart,
    tip: "Don't check P/L every second. Focus on executing the process.",
    detail: "Outcomes follow good process, not the other way around.",
    color: "bg-red-500"
  },
  
  // MINDSET
  {
    category: "MINDSET",
    icon: Brain,
    tip: "Accept that losses are part of the game. Even 60% win rate means 40% losses.",
    detail: "Professional traders think in probabilities, not certainties.",
    color: "bg-purple-600"
  },
  {
    category: "MINDSET",
    icon: Brain,
    tip: "Your job isn't to be right. It's to make money when right & lose little when wrong.",
    detail: "R:R matters more than win rate.",
    color: "bg-purple-600"
  },
  {
    category: "MINDSET",
    icon: Brain,
    tip: "The market doesn't know or care about your position.",
    detail: "Don't take losses personally. It's just probabilities playing out.",
    color: "bg-purple-600"
  },
  {
    category: "MINDSET",
    icon: Brain,
    tip: "Journal every trade. The patterns you'll discover are gold.",
    detail: "Your best teacher is your own trading history.",
    color: "bg-purple-600"
  },
  
  // PATIENCE
  {
    category: "PATIENCE",
    icon: Clock,
    tip: "Boredom is expensive. Don't trade just to feel busy.",
    detail: "The best traders spend 90% waiting, 10% executing.",
    color: "bg-amber-500"
  },
  {
    category: "PATIENCE",
    icon: Clock,
    tip: "Wait for the fat pitch. Not every ball needs to be swung at.",
    detail: "Quality setups > Quantity of trades",
    color: "bg-amber-500"
  },
  {
    category: "PATIENCE",
    icon: Clock,
    tip: "Let winners run. Cutting profits early kills accounts slowly.",
    detail: "Your winners need to pay for your losers AND make profit.",
    color: "bg-amber-500"
  },
  
  // FOCUS
  {
    category: "FOCUS",
    icon: Target,
    tip: "Trade your watchlist, not the entire market.",
    detail: "Know 5 stocks deeply > Know 500 stocks superficially.",
    color: "bg-emerald-600"
  },
  {
    category: "FOCUS",
    icon: Target,
    tip: "Master one strategy before learning another.",
    detail: "Strategy hopping is a sign of not trusting your edge.",
    color: "bg-emerald-600"
  },
  
  // PRO TIPS
  {
    category: "PRO TIP",
    icon: Lightbulb,
    tip: "The first hour is for pros. New traders: wait until 10:30 AM.",
    detail: "Opening volatility eats beginners alive.",
    color: "bg-gradient-to-r from-amber-500 to-orange-500"
  },
  {
    category: "PRO TIP",
    icon: Lightbulb,
    tip: "Never average down on a loser. Add only to winners.",
    detail: "Losers are telling you something. Listen.",
    color: "bg-gradient-to-r from-amber-500 to-orange-500"
  },
  {
    category: "PRO TIP",
    icon: Lightbulb,
    tip: "News creates volatility, not direction. Wait for price to tell you.",
    detail: "Don't predict. React.",
    color: "bg-gradient-to-r from-amber-500 to-orange-500"
  },
  {
    category: "PRO TIP",
    icon: Lightbulb,
    tip: "Green days: trade normal size. Red days: reduce or sit out.",
    detail: "Protect your gains. They're harder to make than to lose.",
    color: "bg-gradient-to-r from-amber-500 to-orange-500"
  }
];

export default function TradingPsychologyTips() {
  const [currentTip, setCurrentTip] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Set initial random tip
    getRandomTip();
    
    // Rotate tip every 30 seconds
    const interval = setInterval(() => {
      getRandomTip();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getRandomTip = () => {
    setIsAnimating(true);
    setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * PSYCHOLOGY_TIPS.length);
      setCurrentTip(PSYCHOLOGY_TIPS[randomIndex]);
      setIsAnimating(false);
    }, 300);
  };

  if (!currentTip) return null;

  const IconComponent = currentTip.icon;

  return (
    <div 
      className={`relative overflow-hidden rounded-lg p-4 transition-opacity duration-300 ${isAnimating ? 'opacity-50' : 'opacity-100'}`}
      style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
      }}
      data-testid="psychology-tip"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${currentTip.color}`}>
              <IconComponent className="w-4 h-4 text-white" />
            </div>
            <div>
              <Badge className={`text-xs ${currentTip.color} border-0`}>
                {currentTip.category}
              </Badge>
              <p className="text-xs text-zinc-400 mt-0.5">Trading Psychology</p>
            </div>
          </div>
          <button 
            onClick={getRandomTip}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Next tip"
          >
            <RefreshCw className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
        
        {/* Main Tip */}
        <p className="text-white font-medium text-sm leading-relaxed mb-2">
          {currentTip.tip}
        </p>
        
        {/* Detail */}
        <p className="text-zinc-400 text-xs italic">
          "{currentTip.detail}"
        </p>
        
        {/* Footer */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
          <Brain className="w-3 h-3 text-zinc-500" />
          <span className="text-xs text-zinc-500">
            80% Psychology • 20% Technical
          </span>
        </div>
      </div>
    </div>
  );
}
